/**
 * Renders an action-button icon from config: a lucide icon by (kebab-case)
 * name, resolved from the single lazily-loaded icon map (see
 * utils/lucideIconMap — one chunk, not one-per-icon), or an uploaded image.
 *
 * Uploaded icons render as <img src> (never inline SVG) so a malicious SVG
 * can't execute when shown to public estimate viewers.
 */

import React, { useEffect, useState } from 'react';
import type { LucideIcon } from 'lucide-react';
import type { ActionButtonIcon } from '../../config/actionButtons';
import { loadLucideIcons, getCachedLucideIcons, toPascalIconName } from '../../utils/lucideIconMap';

interface Props {
  icon: ActionButtonIcon;
  size?: number;
}

export const DynamicActionIcon: React.FC<Props> = ({ icon, size = 22 }) => {
  const lucideName = icon.type === 'lucide' ? toPascalIconName(icon.name) : '';
  const [Comp, setComp] = useState<LucideIcon | null>(() => {
    if (icon.type !== 'lucide') return null;
    const cached = getCachedLucideIcons();
    return cached ? cached[lucideName] ?? null : null;
  });

  useEffect(() => {
    if (icon.type !== 'lucide') { setComp(null); return; }
    let cancelled = false;
    loadLucideIcons().then((map) => { if (!cancelled) setComp(() => map[lucideName] ?? null); });
    return () => { cancelled = true; };
  }, [icon.type, lucideName]);

  if (icon.type === 'upload') {
    return (
      <img
        src={icon.url}
        alt=""
        width={size}
        height={size}
        style={{ objectFit: 'contain', display: 'block' }}
      />
    );
  }
  if (!Comp) {
    // Keeps layout stable while the icon chunk loads (or if the name is unknown).
    return <span style={{ display: 'inline-block', width: size, height: size }} />;
  }
  return <Comp size={size} />;
};

export default DynamicActionIcon;
